import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/lib/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Loader2, Mail, Lock, Eye, EyeOff } from 'lucide-react';

export default function Login() {
  const { isAuthenticated, isLoadingAuth } = useAuth();
  const navigate = useNavigate();

  const [mode, setMode] = useState('login'); // 'login' | 'signup' | 'forgot' | 'forgot-sent'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Redirige si déjà connecté
  useEffect(() => {
    if (!isLoadingAuth && isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isLoadingAuth, navigate]);

  const handleGoogleLogin = async () => {
    setError('');
    setLoading(true);
    const { error } = await supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: import.meta.env.VITE_APP_URL || window.location.origin,
        queryParams: { prompt: 'select_account' }
      }
    });
    if (error) {
      setError(translateError(error.message));
      setLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!email.trim()) {
      setError('Entre ton adresse email.');
      return;
    }
    if (password.length < 6) {
      setError('Le mot de passe doit contenir au moins 6 caractères.');
      return;
    }

    setLoading(true);

    try {
      if (mode === 'signup') {
        const { error } = await supabase.auth.signUp({
          email: email.trim(),
          password,
          options: { emailRedirectTo: import.meta.env.VITE_APP_URL || window.location.origin }
        });
        if (error) {
          setError(translateError(error.message));
          return;
        }
        setError('');
        setMode('confirm');
        return;
      }

      // Login
      const { error } = await supabase.auth.signInWithPassword({
        email: email.trim(),
        password
      });
      if (error) {
        setError(translateError(error.message));
        return;
      }
      navigate('/', { replace: true });
    } catch (err) {
      setError(err.message || 'Une erreur est survenue');
    } finally {
      setLoading(false);
    }
  };

  function translateError(msg) {
    const map = {
      'Invalid login credentials': 'Email ou mot de passe incorrect.',
      'Email not confirmed': "Ton email n'est pas encore confirmé. Vérifie ta boîte mail.",
      'User already registered': 'Un compte existe déjà avec cet email.',
      'Signup requires a valid password': 'Le mot de passe est invalide.',
      'Password should be at least 6 characters': 'Le mot de passe doit contenir au moins 6 caractères.',
      'Unable to validate email address: invalid format': "Le format de l'email est invalide.",
      'Email rate limit exceeded': 'Trop de tentatives. Réessaie dans quelques minutes.',
      'For security purposes, you can only request this after 60 seconds.': 'Attends 60 secondes avant de réessayer.',
    };
    return map[msg] || `Erreur : ${msg}`;
  }

  if (isLoadingAuth) {
    return (
      <div className="min-h-screen bg-[#0A0B10] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#6E2BFF]" />
      </div>
    );
  }

  if (isAuthenticated) return null;

  // Écran de confirmation d'inscription
  // Écran mot de passe oublié
  if (mode === 'forgot') {
    const handleForgotSubmit = async (e) => {
      e.preventDefault();
      if (!email.trim()) { setError('Entre ton adresse email.'); return; }
      setLoading(true);
      setError('');
      const { error: resetError } = await supabase.auth.resetPasswordForEmail(email.trim(), {
        redirectTo: import.meta.env.VITE_APP_URL || window.location.origin
      });
      if (resetError) {
        setError(translateError(resetError.message));
        setLoading(false);
        return;
      }
      setMode('forgot-sent');
      setLoading(false);
    };

    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0B10] to-[#13151D] flex items-center justify-center px-6">
        <div className="w-full max-w-sm space-y-6">
          <div className="text-center space-y-2">
            <h1 className="text-2xl font-bold text-white">Mot de passe oublié</h1>
            <p className="text-[#B3B6C2] text-sm">Entre ton email pour recevoir un lien de réinitialisation.</p>
          </div>
          <form onSubmit={handleForgotSubmit} className="space-y-4">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B3B6C2]" />
              <Input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 bg-[#13151D] border-[#1B1E28] text-white placeholder:text-[#555] rounded-xl focus:border-[#6E2BFF] focus:ring-[#6E2BFF]"
              />
            </div>
            {error && <p className="text-red-400 text-sm text-center">{error}</p>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-semibold rounded-xl transition-all"
            >
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Envoyer le lien'}
            </Button>
          </form>
          <p className="text-center">
            <button
              onClick={() => { setMode('login'); setError(''); }}
              className="text-sm text-[#6E2BFF] hover:text-[#8B5CFF] font-medium transition-colors"
            >
              Retour à la connexion
            </button>
          </p>
        </div>
      </div>
    );
  }

  // Écran confirmation envoi lien reset
  if (mode === 'forgot-sent') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0B10] to-[#13151D] flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#6E2BFF]/20 flex items-center justify-center">
            <Mail className="w-8 h-8 text-[#6E2BFF]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Lien envoyé</h1>
          <p className="text-[#B3B6C2] text-sm leading-relaxed">
            Un lien de réinitialisation a été envoyé à <span className="text-white font-medium">{email}</span>.
            Vérifie ta boîte mail et tes spams.
          </p>
          <Button
            variant="ghost"
            className="text-[#6E2BFF] hover:text-[#8B5CFF] hover:bg-[#6E2BFF]/10"
            onClick={() => { setMode('login'); setError(''); }}
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  // Écran de confirmation d'inscription
  if (mode === 'confirm') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#0A0B10] to-[#13151D] flex items-center justify-center px-6">
        <div className="w-full max-w-sm text-center space-y-6">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-[#6E2BFF]/20 flex items-center justify-center">
            <Mail className="w-8 h-8 text-[#6E2BFF]" />
          </div>
          <h1 className="text-2xl font-bold text-white">Vérifie ta boîte mail</h1>
          <p className="text-[#B3B6C2] text-sm leading-relaxed">
            Un lien de confirmation a été envoyé à <span className="text-white font-medium">{email}</span>.
            Clique dessus pour activer ton compte.
          </p>
          <Button
            variant="ghost"
            className="text-[#6E2BFF] hover:text-[#8B5CFF] hover:bg-[#6E2BFF]/10"
            onClick={() => { setMode('login'); setError(''); }}
          >
            Retour à la connexion
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0A0B10] to-[#13151D] flex flex-col items-center justify-center px-6">
      <div className="w-full max-w-sm space-y-8">
        {/* Logo / Titre */}
        <div className="text-center space-y-2">
          <h1 className="text-4xl font-black tracking-tight text-white">ZNO</h1>
          <p className="text-[#B3B6C2] text-sm">
            {mode === 'login' ? 'Connecte-toi pour jouer.' : 'Crée ton compte pour jouer.'}
          </p>
        </div>

        {/* Bouton Google */}
        <Button
          onClick={handleGoogleLogin}
          disabled={loading}
          className="w-full h-12 bg-white hover:bg-gray-100 text-gray-900 font-medium rounded-xl flex items-center justify-center gap-3 transition-all"
        >
          {loading && mode !== 'login' && mode !== 'signup' ? (
            <Loader2 className="w-5 h-5 animate-spin" />
          ) : (
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
              <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
              <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
              <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
            </svg>
          )}
          Continuer avec Google
        </Button>

        {/* Séparateur */}
        <div className="flex items-center gap-4">
          <div className="flex-1 h-px bg-[#1B1E28]" />
          <span className="text-xs text-[#B3B6C2] uppercase tracking-wider">ou</span>
          <div className="flex-1 h-px bg-[#1B1E28]" />
        </div>

        {/* Formulaire email/password */}
        <form onSubmit={handleEmailSubmit} className="space-y-4">
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B3B6C2]" />
            <Input
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="pl-10 h-12 bg-[#13151D] border-[#1B1E28] text-white placeholder:text-[#555] rounded-xl focus:border-[#6E2BFF] focus:ring-[#6E2BFF]"
            />
          </div>

          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#B3B6C2]" />
            <Input
              type={showPassword ? 'text' : 'password'}
              placeholder="Mot de passe"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pl-10 pr-10 h-12 bg-[#13151D] border-[#1B1E28] text-white placeholder:text-[#555] rounded-xl focus:border-[#6E2BFF] focus:ring-[#6E2BFF]"
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-[#B3B6C2] hover:text-white transition-colors"
            >
              {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>

          {mode === 'login' && (
            <div className="text-right">
              <button
                type="button"
                onClick={() => { setMode('forgot'); setError(''); }}
                className="text-xs text-[#B3B6C2] hover:text-[#6E2BFF] transition-colors"
              >
                Mot de passe oublié ?
              </button>
            </div>
          )}

          {error && (
            <p className="text-red-400 text-sm text-center">{error}</p>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-12 bg-[#6E2BFF] hover:bg-[#8B5CFF] text-white font-semibold rounded-xl transition-all"
          >
            {loading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : mode === 'login' ? (
              'Se connecter'
            ) : (
              "S'inscrire"
            )}
          </Button>
        </form>

        {/* Toggle login/signup */}
        <p className="text-center text-sm text-[#B3B6C2]">
          {mode === 'login' ? (
            <>
              Pas encore de compte ?{' '}
              <button
                onClick={() => { setMode('signup'); setError(''); }}
                className="text-[#6E2BFF] hover:text-[#8B5CFF] font-medium transition-colors"
              >
                S'inscrire
              </button>
            </>
          ) : (
            <>
              Déjà un compte ?{' '}
              <button
                onClick={() => { setMode('login'); setError(''); }}
                className="text-[#6E2BFF] hover:text-[#8B5CFF] font-medium transition-colors"
              >
                Se connecter
              </button>
            </>
          )}
        </p>
      </div>
    </div>
  );
}
